

import { buildUrl, formatParam } from "../util/integration"
import { HTTP_STATUS_SUCCESS, MSG_ERROR_OCCURED } from "../util/constants"



import {
    authorizedFetch,
} from "./http_api";


/**
 * Serialisiert einzelne Query-Parameter.
 *
 * Arrays werden kommasepariert übertragen, sodass Spring sie direkt als
 * List<T> an einen @RequestParam binden kann.
 */
const serializeParam = (value) => {
    if (Array.isArray(value)) {
        return value.join(",");
    }

    return value;
};

/**
 * Erstellt eine URL-Query aus einem Objekt.
 *
 * undefined und null werden nicht übertragen.
 */
const createQuery = (params = {}) => {
    const query = Object.entries(params)
        .filter(([, value]) => {
            return (
                value !== undefined
                && value !== null
            );
        })
        .map(([name, value]) => {
            return (
                `${name}=`
                + formatParam(
                    serializeParam(value),
                )
            );
        })
        .join("&");

    return query
        ? `?${query}`
        : "";
};

/**
 * Erstellt eine URL für den UserWebService.
 */
const userUrl = (
    userId,
    resource = "",
    params = {},
) => {
    const suffix = resource
        ? `/${resource}`
        : "";

    return buildUrl(
        `user/${formatParam(userId)}`
        + suffix
        + createQuery(params),
    );
};

/**
 * Führt einen autorisierten Request gegen einen User-Endpunkt aus.
 */
const authorizedUserRequest = (
    userId,
    accessToken,
    resource = "",
    params = {},
    method = "GET",
) => {
    return authorizedFetch(
        accessToken,
        userUrl(
            userId,
            resource,
            params,
        ),
        {
            method,
        },
    );
};

/**
 * Überträgt ein PDF als rohen Request-Body.
 */
const authorizedPdfRequest = (
    accessToken,
    url,
    pdf,
) => {
    return authorizedFetch(
        accessToken,
        url,
        {
            method: "PUT",
            headers: {
                "Content-Type":
                    "application/pdf",
            },
            body: pdf,
        },
    );
};


export const authenticate = async (
    username,
    accessToken,
    showMessage,
) => {
    let context = null;

    try {
        const response =
            await authorizedFetch(
                accessToken,
                buildUrl(
                    "authentication/authenticate",
                ),
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "text/plain",
                    },
                    body: username,
                },
            );

        if (
            response.status
            === HTTP_STATUS_SUCCESS
        ) {
            context =
                await response.json();
        } else {
            showMessage(
                MSG_ERROR_OCCURED
                + response.status,
            );
        }
    } catch (error) {
        showMessage(
            error.toString(),
        );
    }

    return context;
};


export const isUsernameAvailableAPI =
    async (
        username,
        accessToken,
        showMessage,
    ) => {
        let context = null;

        try {
            const response =
                await authorizedFetch(
                    accessToken,
                    buildUrl(
                        "authentication/isUsernameAvailable",
                    ),
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "text/plain",
                        },
                        body: username,
                    },
                );

            if (
                response.status
                === HTTP_STATUS_SUCCESS
            ) {
                context =
                    await response.json();
            } else {
                showMessage(
                    MSG_ERROR_OCCURED
                    + response.status,
                );
            }
        } catch (error) {
            showMessage(
                error.toString(),
            );
        }

        return context;
    };


/**
 * GET /user/{id}
 */
export const fetchUserAPI = (
    userId,
    accessToken,
) => {
    return authorizedUserRequest(
        userId,
        accessToken,
    );
};


/**
 * GET /user/{id}/profile
 */
export const fetchUserProfileAPI = (
    userId,
    accessToken,
) => {
    return authorizedUserRequest(
        userId,
        accessToken,
        "profile",
    );
};


/**
 * GET /user/{id}/imprint
 *
 * Dieser Backend-Endpunkt erwartet keinen Authorization-Header.
 */
export const fetchUserImprintAPI = (
    userId,
) => {
    return fetch(
        userUrl(
            userId,
            "imprint",
        ),
    );
};


/**
 * GET /user/all
 */
export const fetchUsersAPI = (
    accessToken,
) => {
    return authorizedFetch(
        accessToken,
        buildUrl("user/all"),
    );
};


/**
 * GET /user/buyers
 */
export const fetchBuyersAPI = (
    accessToken,
) => {
    return authorizedFetch(
        accessToken,
        buildUrl("user/buyers"),
    );
};


/**
 * GET /user/{id}/blockedBuyers
 */
export const fetchBlockedBuyersAPI = (
    accessToken,
    userId,
) => {
    return authorizedUserRequest(
        userId,
        accessToken,
        "blockedBuyers",
    );
};


/**
 * POST /user/
 */
export const createUserAPI = (
    accessToken,
    newUsername,
    newPassword,
    mobilePhoneNumber,
    isAdmin,
    isSeller,
    isBuyer,
) => {
    return authorizedFetch(
        accessToken,
        buildUrl(
            "user/"
            + createQuery({
                username:
                    newUsername,

                password:
                    newPassword,

                mobilePhoneNumber,
                isAdmin,
                isSeller,
                isBuyer,
            }),
        ),
        {
            method: "POST",
        },
    );
};


/*
 * Die Credentials werden als roher text/plain-Request-Body übertragen.
 */
export const saveUsernameAPI = (
    userId,
    accessToken,
    newUsername,
) => {
    return authorizedFetch(
        accessToken,
        buildUrl(
            `authentication/${formatParam(userId)}/username`,
        ),
        {
            method: "POST",
            headers: {
                "Content-Type": "text/plain",
            },
            body: newUsername,
        },
    );
};


export const savePasswordAPI = (
    userId,
    accessToken,
    newPassword,
) => {
    return authorizedFetch(
        accessToken,
        buildUrl(
            `authentication/${formatParam(userId)}/password`,
        ),
        {
            method: "POST",
            headers: {
                "Content-Type": "text/plain",
            },
            body: newPassword,
        },
    );
};


/**
 * PUT /user/{id}/businessName
 */
export const saveBusinessNameAPI = (
    userId,
    accessToken,
    businessName,
) => {
    return authorizedUserRequest(
        userId,
        accessToken,
        "businessName",
        {
            businessName,
        },
        "PUT",
    );
};


/**
 * PUT /user/{id}/firstName
 */
export const saveFirstNameAPI = (
    userId,
    accessToken,
    newFirstName,
) => {
    return authorizedUserRequest(
        userId,
        accessToken,
        "firstName",
        {
            firstName:
                newFirstName,
        },
        "PUT",
    );
};


/**
 * PUT /user/{id}/lastName
 */
export const saveLastnameNameAPI = (
    userId,
    accessToken,
    newLastName,
) => {
    return authorizedUserRequest(
        userId,
        accessToken,
        "lastName",
        {
            lastName:
                newLastName,
        },
        "PUT",
    );
};

export const saveAdditionalDirectorsAPI = (
    userId,
    accessToken,
    additionalDirectors,
) => {
    return authorizedUserRequest(
        userId,
        accessToken,
        "additionalDirectors",
        {
            additionalDirectors:
                additionalDirectors,
        },
        "PUT",
    );
};


/**
 * Korrekt geschriebener Exportname für neuen Code.
 */
export const saveLastNameAPI =
    saveLastnameNameAPI;


/**
 * PUT /user/{id}/street
 */
export const saveStreetAPI = (
    userId,
    accessToken,
    newStreet,
) => {
    return authorizedUserRequest(
        userId,
        accessToken,
        "street",
        {
            street:
                newStreet,
        },
        "PUT",
    );
};


/**
 * PUT /user/{id}/houseNo
 */
export const saveHouseNoAPI = (
    userId,
    accessToken,
    newHouseNo,
) => {
    return authorizedUserRequest(
        userId,
        accessToken,
        "houseNo",
        {
            houseNo:
                newHouseNo,
        },
        "PUT",
    );
};


/**
 * PUT /user/{id}/zipCode
 */
export const saveZipCodeAPI = (
    userId,
    accessToken,
    newZipCode,
) => {
    return authorizedUserRequest(
        userId,
        accessToken,
        "zipCode",
        {
            zipCode:
                newZipCode,
        },
        "PUT",
    );
};


/**
 * PUT /user/{id}/town
 */
export const saveTownAPI = (
    userId,
    accessToken,
    newTown,
) => {
    return authorizedUserRequest(
        userId,
        accessToken,
        "town",
        {
            town:
                newTown,
        },
        "PUT",
    );
};


/**
 * PUT /user/{id}/country
 */
export const saveCountryAPI = (
    userId,
    accessToken,
    newCountry,
) => {
    return authorizedUserRequest(
        userId,
        accessToken,
        "country",
        {
            country:
                newCountry,
        },
        "PUT",
    );
};


/**
 * PUT /user/{id}/emailAddress
 */
export const saveEmailAddressAPI = (
    userId,
    accessToken,
    newEmailAddress,
) => {
    return authorizedUserRequest(
        userId,
        accessToken,
        "emailAddress",
        {
            emailAddress:
                newEmailAddress,
        },
        "PUT",
    );
};


/**
 * PUT /user/{id}/mobilePhoneNumber
 */
export const saveMobilePhoneNumberAPI = (
    userId,
    accessToken,
    newMobilePhoneNumber,
) => {
    return authorizedUserRequest(
        userId,
        accessToken,
        "mobilePhoneNumber",
        {
            mobilePhoneNumber:
                newMobilePhoneNumber,
        },
        "PUT",
    );
};


/**
 * PUT /user/{id}/registered
 */
export const saveRegisteredAPI = (
    userId,
    accessToken,
    registered,
) => {
    return authorizedUserRequest(
        userId,
        accessToken,
        "registered",
        {
            registered,
        },
        "PUT",
    );
};


/**
 * PUT /user/{id}/registration_court
 */
export const saveRegistrationCourtAPI = (
    userId,
    accessToken,
    registrationCourt,
) => {
    return authorizedUserRequest(
        userId,
        accessToken,
        "registration_court",
        {
            registrationCourt,
        },
        "PUT",
    );
};


/**
 * PUT /user/{id}/registration_section
 */
export const saveRegistrationSectionAPI = (
    userId,
    accessToken,
    registrationSection,
) => {
    return authorizedUserRequest(
        userId,
        accessToken,
        "registration_section",
        {
            registrationSection,
        },
        "PUT",
    );
};


/**
 * PUT /user/{id}/registration_no
 */
export const saveRegistrationNoAPI = (
    userId,
    accessToken,
    registrationNo,
) => {
    return authorizedUserRequest(
        userId,
        accessToken,
        "registration_no",
        {
            registrationNo,
        },
        "PUT",
    );
};


/**
 * PUT /user/{id}/vatId
 */
export const saveVatIdAPI = (
    userId,
    accessToken,
    vatId,
) => {
    return authorizedUserRequest(
        userId,
        accessToken,
        "vatId",
        {
            vatId,
        },
        "PUT",
    );
};


/**
 * Rückwärtskompatibilität zu bisherigen Exportnamen.
 */
export const saveIsVatIdAPI =
    saveVatIdAPI;

export const saveUstIdAPI =
    saveVatIdAPI;


/**
 * PUT /user/{id}/taxId
 */
export const saveTaxIdAPI = (
    userId,
    accessToken,
    taxId,
) => {
    return authorizedUserRequest(
        userId,
        accessToken,
        "taxId",
        {
            taxId,
        },
        "PUT",
    );
};


/**
 * PUT /user/{id}/vatRate
 */
export const saveVatRateAPI = (
    userId,
    accessToken,
    vatRate,
) => {
    return authorizedUserRequest(
        userId,
        accessToken,
        "vatRate",
        {
            vatRate,
        },
        "PUT",
    );
};


/**
 * PUT /user/{id}/taxExemption
 */
export const saveTaxExemptionAPI = (
    userId,
    accessToken,
    taxExemption,
) => {
    return authorizedUserRequest(
        userId,
        accessToken,
        "taxExemption",
        {
            taxExemption,
        },
        "PUT",
    );
};


/**
 * PUT /user/{id}/bank
 */
export const saveBankAPI = (
    userId,
    accessToken,
    bank,
) => {
    return authorizedUserRequest(
        userId,
        accessToken,
        "bank",
        {
            bank,
        },
        "PUT",
    );
};


/**
 * PUT /user/{id}/bic
 */
export const saveBICAPI = (
    userId,
    accessToken,
    bic,
) => {
    return authorizedUserRequest(
        userId,
        accessToken,
        "bic",
        {
            bic,
        },
        "PUT",
    );
};


/**
 * PUT /user/{id}/iban
 */
export const saveIBANAPI = (
    userId,
    accessToken,
    iban,
) => {
    return authorizedUserRequest(
        userId,
        accessToken,
        "iban",
        {
            iban,
        },
        "PUT",
    );
};


/**
 * PUT /user/{id}/imprint
 */
export const saveImprintAPI = (
    userId,
    accessToken,
    imprint,
) => {
    return authorizedUserRequest(
        userId,
        accessToken,
        "imprint",
        {
            imprint,
        },
        "PUT",
    );
};


/**
 * PUT /user/{id}/portrait
 *
 * Unterstützt sowohl:
 *
 * savePortraitURLAPI(userId, accessToken, portraitUrl)
 *
 * als auch die ältere Signatur:
 *
 * savePortraitURLAPI(userId, username, accessToken, portraitUrl)
 */
export const savePortraitURLAPI = (
    userId,
    accessTokenOrUsername,
    portraitUrlOrAccessToken,
    legacyPortraitUrl,
) => {
    const usesLegacySignature =
        legacyPortraitUrl
        !== undefined;

    const accessToken =
        usesLegacySignature
            ? portraitUrlOrAccessToken
            : accessTokenOrUsername;

    const portraitUrl =
        usesLegacySignature
            ? legacyPortraitUrl
            : portraitUrlOrAccessToken;

    return authorizedUserRequest(
        userId,
        accessToken,
        "portrait",
        {
            portraitUrl,
        },
        "PUT",
    );
};


/**
 * PUT /user/{id}/isAdmin
 */
export const saveIsAdminAPI = (
    userId,
    accessToken,
    isAdmin,
) => {
    return authorizedUserRequest(
        userId,
        accessToken,
        "isAdmin",
        {
            isAdmin,
        },
        "PUT",
    );
};


/**
 * PUT /user/{id}/isBuyer
 */
export const saveIsBuyerAPI = (
    userId,
    accessToken,
    isBuyer,
) => {
    return authorizedUserRequest(
        userId,
        accessToken,
        "isBuyer",
        {
            isBuyer,
        },
        "PUT",
    );
};


/**
 * PUT /user/{id}/isSeller
 */
export const saveIsSellerAPI = (
    userId,
    accessToken,
    isSeller,
) => {
    return authorizedUserRequest(
        userId,
        accessToken,
        "isSeller",
        {
            isSeller,
        },
        "PUT",
    );
};


/**
 * GET /user/{id}/canSell
 *
 * username bleibt aus Kompatibilitätsgründen in der Signatur.
 */
export const fetchUserCanSellAPI = (
    userId,
    username,
    accessToken,
) => {
    void username;

    return authorizedUserRequest(
        userId,
        accessToken,
        "canSell",
    );
};


/**
 * GET /user/{id}/canBuy
 *
 * username bleibt aus Kompatibilitätsgründen in der Signatur.
 */
export const fetchUserCanBuyAPI = (
    userId,
    username,
    accessToken,
) => {
    void username;

    return authorizedUserRequest(
        userId,
        accessToken,
        "canBuy",
    );
};


/**
 * Rückwärtskompatibilität für den bisherigen Tippfehler.
 */
export const fetchUserCanBuylAPI =
    fetchUserCanBuyAPI;


/**
 * PUT /user/{id}/blockages
 */
export const blockBuyerAPI = (
    accessToken,
    sellerId,
    buyerId,
) => {
    return authorizedUserRequest(
        sellerId,
        accessToken,
        "blockages",
        {
            buyerId,
        },
        "PUT",
    );
};


/**
 * DELETE /user/{id}/blockages
 */
export const unblockBuyerAPI = (
    accessToken,
    sellerId,
    buyerId,
) => {
    return authorizedUserRequest(
        sellerId,
        accessToken,
        "blockages",
        {
            buyerId,
        },
        "DELETE",
    );
};


/**
 * PUT /user/{id}/tags
 *
 * Spring bindet den kommaseparierten Wert an List<Long>.
 */
export const saveTagsAPI = (
    userId,
    accessToken,
    tagIds,
) => {
    return authorizedUserRequest(
        userId,
        accessToken,
        "tags",
        {
            tags:
                Array.isArray(tagIds)
                    ? tagIds
                    : [],
        },
        "PUT",
    );
};


/**
 * PUT /user/{id}/transactionFee
 */
export const saveTransactionFeeAPI = (
    userId,
    accessToken,
    transactionFee,
) => {
    return authorizedUserRequest(
        userId,
        accessToken,
        "transactionFee",
        {
            transactionFee,
        },
        "PUT",
    );
};


/**
 * Kompatibilitätsalias zur DTO-Bezeichnung transactionFees.
 */
export const saveTransactionFeesAPI =
    saveTransactionFeeAPI;


/**
 * PUT /user/{id}/invoicePrefix
 */
export const saveInvoicePrefixAPI = (
    userId,
    accessToken,
    prefix,
) => {
    return authorizedUserRequest(
        userId,
        accessToken,
        "invoicePrefix",
        {
            prefix,
        },
        "PUT",
    );
};


/**
 * PUT /user/{id}/terms
 *
 * Content-Type: application/pdf
 */
export const saveSellerTermsAPI = (
    userId,
    accessToken,
    pdf,
) => {
    return authorizedPdfRequest(
        accessToken,
        userUrl(
            userId,
            "terms",
        ),
        pdf,
    );
};


/**
 * PUT /user/{id}/privacyPolicy
 *
 * Content-Type: application/pdf
 */
export const saveSellerPrivacyPolicyAPI = (
    userId,
    accessToken,
    pdf,
) => {
    return authorizedPdfRequest(
        accessToken,
        userUrl(
            userId,
            "privacyPolicy",
        ),
        pdf,
    );
};


/**
 * PUT /user/operator/terms
 *
 * Der Operator wird aus dem Access Token ermittelt.
 * Deshalb wird keine User-ID übergeben.
 *
 * Content-Type: application/pdf
 */
export const saveOperatorTermsAPI = (
    accessToken,
    pdf,
) => {
    return authorizedPdfRequest(
        accessToken,
        buildUrl(
            "user/operator/terms",
        ),
        pdf,
    );
};


/**
 * PUT /user/operator/privacyPolicy
 *
 * Der Operator wird aus dem Access Token ermittelt.
 * Deshalb wird keine User-ID übergeben.
 *
 * Content-Type: application/pdf
 */
export const saveOperatorPrivacyPolicyAPI = (
    accessToken,
    pdf,
) => {
    return authorizedPdfRequest(
        accessToken,
        buildUrl(
            "user/operator/privacyPolicy",
        ),
        pdf,
    );
};


/**
 * PUT /user/{id}/disable
 */
export const disableAPI = (
    userId,
    accessToken,
) => {
    return authorizedUserRequest(
        userId,
        accessToken,
        "disable",
        {},
        "PUT",
    );
};


/**
 * Im Spring-Controller existiert kein Endpunkt
 * /nonRegisteredSoleTrader.
 *
 * Stattdessen wird das Backend-Feld "registered" verwendet und der
 * bisherige Frontend-Wert entsprechend invertiert.
 */
export const saveNonRegisteredSoleTraderAPI = (
    userId,
    accessToken,
    nonRegistered,
) => {
    return saveRegisteredAPI(
        userId,
        accessToken,
        !nonRegistered,
    );
};

/**
 * GET /user/registration/courts
 */
export const fetchRegistrationCourtsAPI = (
    accessToken,
) => {
    return authorizedFetch(
        accessToken,
        buildUrl(
            "user/registration/courts",
        ),
    );
};


/**
 * GET /user/registration/sections
 */
export const fetchRegistrationSectionsAPI = (
    accessToken,
) => {
    return authorizedFetch(
        accessToken,
        buildUrl(
            "user/registration/sections",
        ),
    );
};


/**
 * Dieser Endpoint gehört zum Contract-Controller und nicht zum
 * bereitgestellten UserWebService.
 */
export const acceptOfferAPI = (
    userId,
    username,
    accessToken,
) => {
    return authorizedFetch(
        accessToken,
        buildUrl(
            `contract/${formatParam(userId)}/accept`
            + createQuery({
                username,
            }),
        ),
        {
            method: "PUT",
        },
    );
};

/**
 * PUT /user/{id}/stripeId
 */
export const saveStripeIdAPI = (
    userId,
    accessToken,
    stripeId,
) => {
    return authorizedUserRequest(
        userId,
        accessToken,
        "stripeId",
        {
            stripeId,
        },
        "PUT",
    );
};